import { useState } from "react";
import { Audio } from "expo-av";
import { Platform } from "react-native";
import Sentry from "../utils/sentry";
import { getValidAccessToken, refreshAccessToken } from "../utils/authRefresh";

interface StreamErrorPayload {
  title: string;
  message: string;
  onRetry?: () => void;
}

interface UseAITutorAudioInputParams {
  isStreaming: boolean;
  isAttachmentLoading: boolean;
  onSubmit: () => void;
  sendMessageToAxio: (message: string) => Promise<void>;
  onStreamError?: (payload: StreamErrorPayload) => void;
}

function useAITutorAudioInput({
  isStreaming,
  isAttachmentLoading,
  onSubmit,
  sendMessageToAxio,
  onStreamError,
}: UseAITutorAudioInputParams) {
  const [recording, setRecording] = useState<Audio.Recording | null>(null);
  const [isTranscribing, setIsTranscribing] = useState(false);

  const transcribeAudioToText = async (
    audioSource: Blob | { uri: string; name: string; type: string }
  ): Promise<string> => {
    const token = await getValidAccessToken({ minTtlSeconds: 30 });
    if (!token) {
      throw new Error("Missing access token for audio transcription");
    }

    const buildFormData = () => {
      const formData = new FormData();
      if (Platform.OS === "web") {
        formData.append("audio", audioSource as Blob, "recording.webm");
      } else {
        formData.append("audio", audioSource as unknown as Blob);
      }
      return formData;
    };

    let response = await fetch("/api/audio/transcribe", {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: buildFormData(),
    });

    if (response.status === 401 || response.status === 403) {
      const refreshed = await refreshAccessToken();
      if (refreshed?.accessToken) {
        response = await fetch("/api/audio/transcribe", {
          method: "POST",
          headers: {
            Authorization: `Bearer ${refreshed.accessToken}`,
          },
          body: buildFormData(),
        });
      }
    }

    if (!response.ok) {
      throw new Error(`Transcription failed with status ${response.status}`);
    }

    const data = await response.json();
    return (data?.text || "").trim();
  };

  const startRecording = async () => {
    try {
      await Audio.setAudioModeAsync({
        allowsRecordingIOS: true,
        playsInSilentModeIOS: true,
      });

      const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
      const { recording: expoAvRecording } = await Audio.Recording.createAsync({
        ...Audio.RecordingOptionsPresets.HIGH_QUALITY,
        web: {
          mimeType: isSafari ? "video/mp4" : "audio/webm",
          bitsPerSecond: 128000,
        },
      });
      setRecording(expoAvRecording);
    } catch (error) {
      Sentry.captureException(error);
    }
  };

  const stopRecording = async () => {
    if (!recording) {
      return;
    }

    setIsTranscribing(true);

    try {
      await recording.stopAndUnloadAsync();
      const uri = recording.getURI();
      setRecording(null);
      if (!uri) {
        return;
      }

      let audioSource: Blob | { uri: string; name: string; type: string };

      if (Platform.OS === "web") {
        const response = await fetch(uri);
        const blob = await response.blob();
        const mimeType = blob.type || "audio/webm";
        audioSource = new File([blob], "recording.webm", { type: mimeType });
      } else {
        audioSource = {
          uri,
          name: "recording.m4a",
          type: "audio/m4a",
        };
      }

      const transcript = await transcribeAudioToText(audioSource);
      if (!transcript) {
        onStreamError?.({
          title: "No speech detected",
          message: "We could not detect any speech in your recording. Please try again.",
        });
        return;
      }

      await sendMessageToAxio(transcript);
      onSubmit();
    } catch (error: any) {
      Sentry.captureException(error);
      onStreamError?.({
        title: "Failed to process recording",
        message: error?.message || "An error occurred while transcribing your audio. Please try again.",
      });
    } finally {
      setIsTranscribing(false);
    }
  };

  const handleRecordingStopPress = () => {
    if (isStreaming || isTranscribing) {
      return;
    }
    void stopRecording();
  };

  const handleRecordingStartPress = () => {
    if (isStreaming || isAttachmentLoading || isTranscribing) {
      return;
    }
    void startRecording();
  };

  return {
    isRecording: Boolean(recording),
    isTranscribing,
    handleRecordingStopPress,
    handleRecordingStartPress,
  };
}

export default useAITutorAudioInput;
